import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ApiError } from "@/lib/api";
import { useCreateAdminUser, type Role } from "@/queries/adminUsers";

import { classifyApiError } from "./errors";
import {
  PasswordChoice,
  type ManualPasswordState,
  type PasswordMode,
  validateManual,
} from "./PasswordChoice";
import { PasswordRevealOnce } from "./PasswordRevealOnce";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface CreateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateUserDialog({ open, onOpenChange }: CreateUserDialogProps) {
  const { t } = useTranslation("admin-users");
  const create = useCreateAdminUser();

  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Role>("user");
  const [mode, setMode] = useState<PasswordMode>("manual");
  const [manual, setManual] = useState<ManualPasswordState>({
    password: "",
    confirm: "",
  });
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);

  const userRadioId = useId();
  const adminRadioId = useId();
  const emailInputId = useId();
  const displayNameInputId = useId();

  const reset = () => {
    setEmail("");
    setDisplayName("");
    setRole("user");
    setMode("manual");
    setManual({ password: "", confirm: "" });
    setErrorKey(null);
    setGeneratedPassword(null);
    create.reset();
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorKey(null);

    if (!EMAIL_RE.test(email.trim())) {
      setErrorKey("emailInvalid");
      return;
    }

    if (mode === "manual") {
      const v = validateManual(manual);
      if (v) {
        setErrorKey(v);
        return;
      }
    }

    create.mutate(
      {
        email: email.trim(),
        display_name: displayName.trim() === "" ? null : displayName.trim(),
        role,
        ...(mode === "manual"
          ? { password: manual.password }
          : { generate: true }),
      },
      {
        onSuccess: (data) => {
          if (data.generated_password) {
            setGeneratedPassword(data.generated_password);
          } else {
            handleOpenChange(false);
          }
        },
        onError: (err) => {
          const bucket = classifyApiError(err);
          if (bucket === "duplicate") setErrorKey("duplicate");
          else if (err instanceof ApiError && err.status === 422)
            setErrorKey("emailInvalid");
          else setErrorKey("generic");
        },
      },
    );
  };

  const submitDisabled =
    create.isPending ||
    !email ||
    (mode === "manual" && (!manual.password || !manual.confirm));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("create.title")}</DialogTitle>
          <DialogDescription>{t("create.description")}</DialogDescription>
        </DialogHeader>

        {generatedPassword ? (
          <div className="flex flex-col gap-4">
            <Alert>
              <AlertDescription>{t("create.successHeading")}</AlertDescription>
            </Alert>
            <PasswordRevealOnce
              password={generatedPassword}
              onConfirmHide={() => {
                setGeneratedPassword(null);
                handleOpenChange(false);
              }}
            />
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
            <div className="flex flex-col gap-2">
              <Label htmlFor={emailInputId}>{t("create.emailLabel")}</Label>
              <Input
                id={emailInputId}
                type="email"
                autoComplete="off"
                required
                placeholder={t("create.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={create.isPending}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor={displayNameInputId}>
                {t("create.displayNameLabel")}{" "}
                <span className="text-muted-foreground">
                  {t("create.displayNameOptional")}
                </span>
              </Label>
              <Input
                id={displayNameInputId}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t("create.displayNamePlaceholder")}
                maxLength={120}
                disabled={create.isPending}
              />
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium leading-none">
                {t("create.roleLabel")}
              </span>
              <RadioGroup
                value={role}
                onValueChange={(v) => setRole(v as Role)}
                disabled={create.isPending}
                className="grid grid-cols-2 gap-2"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="user" id={userRadioId} />
                  <Label htmlFor={userRadioId} className="cursor-pointer text-sm">
                    {t("create.roleUser")}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="admin" id={adminRadioId} />
                  <Label htmlFor={adminRadioId} className="cursor-pointer text-sm">
                    {t("create.roleAdmin")}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <PasswordChoice
              mode={mode}
              onModeChange={setMode}
              manual={manual}
              onManualChange={setManual}
              disabled={create.isPending}
            />

            {errorKey ? (
              <Alert variant="destructive">
                <AlertDescription>{t(`create.errors.${errorKey}`)}</AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={create.isPending}
              >
                {t("create.cancel")}
              </Button>
              <Button type="submit" disabled={submitDisabled}>
                {create.isPending ? t("create.submitting") : t("create.submit")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
